/**
 * PresenceHeartbeat — keeps the current user's `presence_state` row marked
 * online by calling the mp module's `heartbeat({ presenceId })` reducer
 * every few seconds.
 *
 * Without this, the server eventually marks our row `isConnected = false`
 * once we miss its expected heartbeat window, and we drop out of every
 * other client's Multiplayer User dropdown. Subscriptions still deliver
 * inserts/updates/deletes in real time — this just ensures our own row
 * stays "alive" while we're signed in.
 *
 * Runs side-effects only; renders nothing.
 */

import { useEffect, useMemo } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import {
  tables as mpTables,
  type DbConnection as MpConn,
} from '@sdk/multiplayer';

const HEARTBEAT_INTERVAL_MS = 5_000;

export function PresenceHeartbeat() {
  const mpState = useSpacetimeDB('mp');
  const [allPresences] = useTable('mp', mpTables.presence_state);

  // Find our own presence row by comparing user_identity to the active
  // connection's identity. SpacetimeDB stamps the row with the caller's
  // identity at `register_presence` time.
  const myPresence = useMemo(() => {
    const myHex = mpState.identity?.toHexString();
    if (!myHex) return undefined;
    return allPresences.find((p) => p.userIdentity.toHexString() === myHex);
  }, [allPresences, mpState.identity]);

  const myPresenceId = myPresence?.presenceId;

  useEffect(() => {
    if (!mpState.isActive || !myPresenceId) return;
    const conn = mpState.getConnection() as MpConn | null;
    if (!conn) return;

    function ping() {
      try {
        conn?.reducers.heartbeat({ presenceId: myPresenceId! });
      } catch (err) {
        console.error('[mp] heartbeat failed', err);
      }
    }
    // Fire once immediately so the row goes connected without waiting a
    // full interval, then on a fixed cadence.
    ping();
    const id = setInterval(ping, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [mpState.isActive, mpState, myPresenceId]);

  return null;
}
